import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  startRun, sendInput, openSession, closeSession, getSessionState, getRun, runScreenshotUrl, stepScreenshotUrl,
  type AgentStep, type InputRequest, type InputPayload, type RunDetail,
} from '../../services/browserAgentService';
import {
  listSites, findSiteByDomain, getDefaultLogin, getSite, addLogin,
  type AgentSite, type SiteLogin,
} from './sitesStore';
import { getSkill, saveSkill, deleteSkill, type AgentSkill, type FieldDef } from './skillsStore';
import '../../styles/BrowserAgent.css';

type ExtractCfg = { schema: FieldDef[]; output?: 'table' | 'json'; maxPages?: number };

// Persisted pointer to the live session, so leaving & returning resumes it.
const ACTIVE_KEY = 'ba_active_session';
function loadActiveSession(): { sessionId: string; startUrl: string } | null {
  try { const r = localStorage.getItem(ACTIVE_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}
function saveActiveSession(v: { sessionId: string; startUrl: string }): void {
  try { localStorage.setItem(ACTIVE_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}
function clearActiveSession(): void {
  try { localStorage.removeItem(ACTIVE_KEY); } catch { /* ignore */ }
}

const ACTION_ICON: Record<string, string> = {
  navigate: '🧭', click: '👆', type: '⌨️', fill_form: '📝', scroll: '↕️', wait: '⏳',
  extract: '📄', credentials: '🔐', user_input: '💬', ask_user: '💬',
  finish: '✅', fail: '⛔',
};


/**
 * Center (main-area) chat for the Browser Agent: task input + live run timeline.
 * Run history lives in the right sidepanel (BrowserAgentSidebarPanel). When a run
 * finishes we emit `ba:refresh-history` so the panel reloads.
 */
const BrowserAgentPage: React.FC = () => {
  const [task, setTask] = useState('');
  const [activeTask, setActiveTask] = useState('');
  const [startUrl, setStartUrl] = useState('');  // no default — the user picks a start site
  const [sites, setSites] = useState<AgentSite[]>(() => listSites());
  const [needsLoginSite, setNeedsLoginSite] = useState<AgentSite | null>(null);
  const [sessionId, setSessionId] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [openingMsg, setOpeningMsg] = useState('Opening…');
  const [viewingRun, setViewingRun] = useState<RunDetail | null>(null);
  const [skillPage, setSkillPage] = useState<{ id?: string; name: string; prompt: string } | null>(null);
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState<AgentStep[]>([]);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [pageTitle, setPageTitle] = useState<string>('');
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);  // fullscreen lightbox image (any source)
  // Per-step screenshots for the CURRENT live run, newest last (history filmstrip).
  // `label` overrides the numeric badge (used for the opening Login/Start frames).
  const [shots, setShots] = useState<{ stepNum: number; src: string; label?: string }[]>([]);
  const [result, setResult] = useState<{ status: string; output: string; data?: any } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [inputReq, setInputReq] = useState<InputRequest | null>(null);
  // Schema-first extraction state for the active run.
  const [activeSchema, setActiveSchema] = useState<FieldDef[] | null>(null);
  const [activeOutput, setActiveOutput] = useState<'table' | 'json'>('table');
  const [schemaErrors, setSchemaErrors] = useState<any[]>([]);
  const [extractProgress, setExtractProgress] = useState<{ count: number; page: number } | null>(null);
  // Resume-after-interruption: if a run dies (network/stream drop) the browser session stays
  // open server-side, so we can continue a fresh step-loop from the current page.
  const [canResume, setCanResume] = useState(false);
  const lastRunRef = useRef<{ task: string; startUrl: string; extract?: ExtractCfg } | null>(null);
  const autoResumedRef = useRef(false);   // auto-resume at most once per run-chain
  const runErroredRef = useRef(false);
  const resumeRef = useRef<() => void>(() => {});
  // Accumulated step history for the current session — seeds each run so context
  // (and the on-screen timeline) carries over instead of restarting. Cleared on Change site.
  const stepsRef = useRef<AgentStep[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);  // cancels the in-flight run / open

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [steps, result, inputReq, screenshot]);

  // Esc closes the fullscreen lightbox.
  useEffect(() => {
    if (!zoomSrc) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoomSrc(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomSrc]);

  // Re-run requests come from the history detail view in the right panel.
  // Keep the editable site list in sync with the Sites manager (right panel).
  useEffect(() => {
    const h = () => setSites(listSites());
    window.addEventListener('ba:sites-changed', h);
    return () => window.removeEventListener('ba:sites-changed', h);
  }, []);

  // Resume the last live session on mount (so leaving & returning lands in the same spot).
  // The session is NOT closed on unmount — it stays open server-side until you Change site.
  useEffect(() => {
    const saved = loadActiveSession();
    if (!saved?.sessionId || !saved.startUrl) return;
    setStartUrl(saved.startUrl);
    setSessionId(saved.sessionId);
    setOpeningMsg('Resuming your session…');
    setPreviewing(true);
    getSessionState(saved.sessionId).then((st) => {
      if (st.alive) {
        if (st.imageB64) setScreenshot(`data:image/jpeg;base64,${st.imageB64}`);
        if (st.url) setCurrentUrl(st.url);
      } else {
        clearActiveSession();
        setSessionId('');
        setStartUrl('');
      }
      setPreviewing(false);
    });
  }, []);

  // Current run id, via ref, so the credential auto-responder isn't a stale closure.
  const runIdRef = useRef('');
  runIdRef.current = runId ?? '';

  const runRef = useRef<(taskArg?: string, startUrlArg?: string, sessionIdArg?: string, extract?: ExtractCfg, isResume?: boolean) => void>(() => {});
  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (!d?.task) return;
      if (d.startUrl) setStartUrl(d.startUrl);
      runRef.current(d.task, d.startUrl);
    };
    window.addEventListener('ba:rerun', h);
    return () => window.removeEventListener('ba:rerun', h);
  }, []);

  // Skills (run a saved prompt in the current session) + open a past run in the center.
  useEffect(() => {
    const onSkill = async (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const p = d.prompt || '';
      const extract: ExtractCfg | undefined =
        Array.isArray(d.schema) && d.schema.length
          ? { schema: d.schema, output: d.output, maxPages: d.maxPages }
          : undefined;
      if (!p && !extract) return;
      setViewingRun(null);
      setSkillPage(null);
      // Site-associated skill: open (and log in to) that site first, then run there.
      if (d.siteId) {
        const site = getSite(d.siteId);
        if (site) {
          const def = getDefaultLogin(site);
          setStartUrl(site.loginUrl);
          setTask(p);
          const sid = await openSiteSession(site, def);
          runRef.current(p, site.loginUrl, sid || undefined, extract);
          return;
        }
      }
      setTask(p);
      runRef.current(p, undefined, undefined, extract);  // runs if a session/start site is active; else pre-fills
    };
    const onViewRun = (e: Event) => {
      const id = (e as CustomEvent).detail?.runId;
      if (!id) return;
      setSkillPage(null);
      getRun(id).then((d) => { if (d) setViewingRun(d); });
    };
    const onOpenSkill = (e: Event) => {
      const id = (e as CustomEvent).detail?.skillId;
      if (!id) return;
      setViewingRun(null);
      if (id === 'new') { setSkillPage({ name: '', prompt: '' }); return; }
      const s = getSkill(id);
      if (s) setSkillPage({ id: s.id, name: s.name, prompt: s.prompt });
    };
    window.addEventListener('ba:run-skill', onSkill);
    window.addEventListener('ba:view-run', onViewRun);
    window.addEventListener('ba:open-skill', onOpenSkill);
    return () => {
      window.removeEventListener('ba:run-skill', onSkill);
      window.removeEventListener('ba:view-run', onViewRun);
      window.removeEventListener('ba:open-skill', onOpenSkill);
    };
  }, []);

  async function handleRun(taskArg?: string, startUrlArg?: string, sessionIdArg?: string, extract?: ExtractCfg, isResume = false) {
    let t = (taskArg ?? task).trim();
    // Extract templates may have a light/empty navigation prompt — synthesize a task.
    if (!t && extract?.schema?.length) {
      t = 'Extract the list of records shown here, paging through every result.';
    }
    const url = startUrlArg ?? startUrl;
    const sid = sessionIdArg ?? sessionId;
    if (!t || !url || running) return;
    setCanResume(false);
    runErroredRef.current = false;
    if (!isResume) {
      autoResumedRef.current = false;
      // Remember the base task so a resume can continue it from the current page.
      lastRunRef.current = { task: t, startUrl: url, extract };
    }
    setTask('');          // clear the input on send so a stray Enter never re-submits it
    setActiveTask(t);
    setViewingRun(null);
    setSkillPage(null);
    setRunning(true);
    setPreviewing(false);
    // Keep prior steps so history carries over (the run seeds from them); don't restart the timeline.
    const historySeed = stepsRef.current.slice(-40);
    // Keep the current screenshot/URL visible — live frames will replace it as the run proceeds.
    setResult(null);
    setError(null);
    setInputReq(null);
    setRunId(null);
    setActiveSchema(extract?.schema?.length ? extract.schema : null);
    setActiveOutput(extract?.output === 'json' ? 'json' : 'table');
    setSchemaErrors([]);
    setExtractProgress(null);

    const controller = new AbortController();
    abortRef.current = controller;
    await startRun(t, url, {
      onRunId: (id) => setRunId(id),
      onStep: (s) => { setSteps((prev) => [...prev, s]); stepsRef.current = [...stepsRef.current, s]; },
      onScreenshot: (n, b64, url) => {
        const src = `data:image/jpeg;base64,${b64}`;
        setScreenshot(src);
        if (url) setCurrentUrl(url);
        // Keep every frame for this run's screenshot-history filmstrip. If a step emits a
        // newer frame (e.g. the filled login form after we fill it), update its thumbnail.
        setShots((prev) =>
          prev.some((s) => s.stepNum === n)
            ? prev.map((s) => (s.stepNum === n ? { ...s, src } : s))
            : [...prev, { stepNum: n, src }]
        );
      },
      onResult: (status, output, data, errs) => { setResult({ status, output, data }); setSchemaErrors(errs || []); },
      onExtractProgress: (count, page) => setExtractProgress({ count, page }),
      onError: (msg) => { runErroredRef.current = true; setError(msg); if (sid) setCanResume(true); },
      onInputRequest: (req) => {
        // Auto-login: if this site has a default login, answer the credential
        // prompt automatically without showing the card.
        if (req.inputKind === 'credentials') {
          const def = getDefaultLogin(findSiteByDomain(req.domain));
          if (def && runIdRef.current) {
            sendInput(runIdRef.current, { kind: 'credentials', username: def.username, password: def.password, extraFields: def.extraFields });
            return;
          }
        }
        setInputReq(req);
      },
      onDone: (status) => {
        setRunning(false);
        if (abortRef.current === controller) abortRef.current = null;
        if (status === 'stopped') { setActiveTask(''); return; }  // user stopped — no auto-resume
        window.dispatchEvent(new CustomEvent('ba:refresh-history'));
        // Auto-resume once if the run died with a session still open.
        if (runErroredRef.current && sid && !autoResumedRef.current) {
          autoResumedRef.current = true;
          setTimeout(() => resumeRef.current?.(), 1200);
        }
      },
    }, sid || undefined, extract, historySeed, controller.signal);
  }

  // Stop the in-flight run (or a slow site open). Aborts the request, which closes the
  // SSE stream and cancels the agent loop server-side; the live browser session stays open.
  function stop() {
    abortRef.current?.abort();
    abortRef.current = null;
    setRunning(false);
    setPreviewing(false);
  }

  runRef.current = handleRun;

  // Continue an interrupted run from the current page, in the same live session.
  async function resume() {
    const args = lastRunRef.current;
    if (!args || running) return;
    setCanResume(false);
    if (sessionId) {
      const st = await getSessionState(sessionId);
      if (!st.alive) {
        setError('The browser session was lost (the server may have restarted). Click “Change site” to reopen, then run again.');
        return;
      }
      if (st.imageB64) setScreenshot(`data:image/jpeg;base64,${st.imageB64}`);
      if (st.url) setCurrentUrl(st.url);
    }
    const resumeTask = `${args.task}\n\n(Resuming a previous attempt — continue from the CURRENT page. Some fields may already be filled; do not redo completed steps, just continue where it left off.)`;
    runRef.current(resumeTask, args.startUrl, sessionId || undefined, args.extract, true);
  }
  resumeRef.current = resume;

  async function submitInput(payload: InputPayload) {
    if (!runId) return;
    setInputReq(null);
    await sendInput(runId, payload);
  }

  function resetRun() {
    setSteps([]); stepsRef.current = []; setScreenshot(null); setCurrentUrl(''); setResult(null); setShots([]);
    setError(null); setInputReq(null); setRunId(null); setActiveTask(''); setTask(''); setPreviewing(false);
    setActiveSchema(null); setSchemaErrors([]); setExtractProgress(null);
    setCanResume(false);
  }

  // When a site is chosen, open a LIVE session on it and show the page so it's ready.
  // The agent run then continues in this same browser instead of restarting.
  async function openSiteSession(site: AgentSite, login?: SiteLogin): Promise<string> {
    setViewingRun(null);
    setSkillPage(null);
    setPageTitle('');
    const verb = login?.username ? `Logging in to ${site.name}` : `Opening ${site.name}`;
    setOpeningMsg(`${verb}…`);
    setPreviewing(true);
    setError(null);
    // The backend waits until the page is fully loaded before returning, which can take a
    // while for heavy apps (e.g. AMS360). Surface staged progress so it never looks frozen.
    const timers = [
      window.setTimeout(() => setOpeningMsg(`Waiting for ${site.name} to finish loading…`), 3000),
      window.setTimeout(() => setOpeningMsg(`${site.name} is taking a while — still loading…`), 9000),
      window.setTimeout(() => setOpeningMsg(`Still loading ${site.name} (large pages can take 15–20s)…`), 16000),
      window.setTimeout(() => setOpeningMsg(`${site.name} may be running a security/system check — waiting for it to finish…`), 23000),
      window.setTimeout(() => setOpeningMsg(`Still waiting on ${site.name}'s security check…`), 38000),
    ];
    const controller = new AbortController();
    abortRef.current = controller;
    let s;
    try {
      s = await openSession(site.loginUrl, login?.username, login?.password, login?.extraFields, controller.signal);
    } finally {
      timers.forEach(clearTimeout);
      if (abortRef.current === controller) abortRef.current = null;
    }
    if (s.aborted) { setPreviewing(false); return ''; }  // user stopped the open
    if (s.sessionId && s.imageB64) {
      setSessionId(s.sessionId);
      saveActiveSession({ sessionId: s.sessionId, startUrl: site.loginUrl });
      setScreenshot(`data:image/jpeg;base64,${s.imageB64}`);
      if (s.url) setCurrentUrl(s.url);
      setPageTitle(s.title || '');
      // Seed the screenshot history with the opening frames so you can review them:
      // the login page (captured before auto-login) then the landed/logged-in page.
      // Negative/zero step nums never collide with run steps (which start at 1).
      const attempted = !!login?.username;
      const finalLabel = !attempted ? 'Start' : s.loggedIn ? 'Logged in' : 'Sign-in';
      const seed: { stepNum: number; src: string; label?: string }[] = [];
      if (s.loginImageB64) seed.push({ stepNum: -2, src: `data:image/jpeg;base64,${s.loginImageB64}`, label: 'Login' });
      if (s.filledImageB64) seed.push({ stepNum: -1, src: `data:image/jpeg;base64,${s.filledImageB64}`, label: 'Filled' });
      seed.push({ stepNum: 0, src: `data:image/jpeg;base64,${s.imageB64}`, label: finalLabel });
      setShots(seed);
      setPreviewing(false);
      // If we tried to log in but we're still on the sign-in page, say so (with the site's
      // own message) instead of pretending it worked.
      if (attempted && !s.loggedIn) {
        setError(
          `Opened ${site.name}, but sign-in didn't complete — still on the login page.` +
          (s.loginError ? ` ${site.name} says: “${s.loginError}”` : '') +
          ` Check the saved password${login?.extraFields?.length ? '/fields' : ''}, or finish signing in (e.g. a 2FA code) in the view above.`
        );
      }
      return s.sessionId;
    }
    setError(`Couldn't open ${site.name} at ${site.loginUrl}. ${s.error || 'No page returned.'} — fix its login URL in the Sites & Logins panel.`);
    setPreviewing(false);
    return '';
  }

  // Open a LIVE session at an arbitrary URL WITHOUT attempting any login.
  async function openUrlSession(rawUrl: string): Promise<void> {
    let url = (rawUrl || '').trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    resetRun();
    setNeedsLoginSite(null);
    setViewingRun(null);
    setSkillPage(null);
    setStartUrl(url);
    setOpeningMsg(`Opening ${hostOf(url)}…`);
    setPreviewing(true);
    setError(null);
    const s = await openSession(url);  // no username/password => no login is attempted
    if (s.sessionId && s.imageB64) {
      setSessionId(s.sessionId);
      saveActiveSession({ sessionId: s.sessionId, startUrl: url });
      setScreenshot(`data:image/jpeg;base64,${s.imageB64}`);
      if (s.url) setCurrentUrl(s.url);
    } else {
      setError(`Couldn't open ${url}. ${s.error || 'No page returned.'}`);
    }
    setPreviewing(false);
  }

  function changeSite() {
    if (sessionId) closeSession(sessionId);
    clearActiveSession();
    setSessionId('');
    resetRun();
    setStartUrl('');
  }

  function onTaskKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleRun();
    }
  }

  return (
    <div className="ba-center">
      {skillPage ? (
        <SkillPage
          initial={skillPage}
          onRun={(p) => { setSkillPage(null); setTask(p); runRef.current(p); }}
          onClose={() => setSkillPage(null)}
        />
      ) : viewingRun ? (
        <RunView run={viewingRun} onBack={() => setViewingRun(null)} />
      ) : !startUrl ? (
        <SitePicker
          sites={sites}
          notice={needsLoginSite ? `No login saved for ${needsLoginSite.name} — add one in the Sites & Logins panel →` : ''}
          onOpenUrl={openUrlSession}
          onPick={(site) => {
            const def = getDefaultLogin(site);
            if (!def) {
              setNeedsLoginSite(site);
              window.dispatchEvent(new CustomEvent('ba:add-login', { detail: { siteId: site.id } }));
              return;
            }
            resetRun();
            setNeedsLoginSite(null);
            setStartUrl(site.loginUrl);
            openSiteSession(site, def);
          }}
        />
      ) : (
      <div className="ba-center__col">
        {currentUrl && <CopyableUrl url={currentUrl} />}
        <div className="ba-center__scroll">
          {activeTask && (
            <div className="ba-activetask"><span className="ba-activetask__icon">▶</span>{activeTask}</div>
          )}
          {previewing && (
            <div className="ba-panel__empty">
              <div className="ba-panel__thinking"><span /><span /><span /></div>
              <p>{openingMsg}</p>
              <button className="ba-panel__stop ba-stop--inline" onClick={stop} title="Stop opening">
                <span className="ba-stop-icon" /> Stop
              </button>
            </div>
          )}

          {!previewing && !activeTask && steps.length === 0 && !running && !error && !result && !screenshot && (
            <div className="ba-panel__empty">
              <span className="ba-panel__empty-icon">🌐</span>
              <p>Describe a task and the agent will begin on this page, then navigate,
                click, and type from page to page.</p>
            </div>
          )}

          {screenshot && (
            <div className="ba-panel__shot ba-center__shot">
              <img
                src={screenshot}
                alt="Browser view"
                className="ba-shot__img--zoomable"
                title="Click to view fullscreen"
                onClick={() => setZoomSrc(screenshot)}
              />
              {running && <span className="ba-panel__shot-live">● live</span>}
              <button
                type="button"
                className="ba-shot__expand"
                title="View fullscreen"
                onClick={() => setZoomSrc(screenshot)}
              >⛶</button>
            </div>
          )}

          {/* Screenshot history for this chat — a thumbnail per step, click to enlarge. */}
          {shots.length > 1 && (
            <div className="ba-filmstrip" aria-label="Screenshot history">
              {shots.map((s) => (
                <button
                  key={s.stepNum}
                  type="button"
                  className="ba-filmstrip__item"
                  title={`${s.label ?? `Step ${s.stepNum}`} — click to enlarge`}
                  onClick={() => setZoomSrc(s.src)}
                >
                  <img src={s.src} alt={s.label ?? `Step ${s.stepNum}`} />
                  <span className="ba-filmstrip__num">{s.label ?? s.stepNum}</span>
                </button>
              ))}
            </div>
          )}

          {zoomSrc && (
            <div className="ba-lightbox" onClick={() => setZoomSrc(null)}>
              <button type="button" className="ba-lightbox__close" title="Close (Esc)" onClick={() => setZoomSrc(null)}>✕</button>
              <img
                src={zoomSrc}
                alt="Browser view (fullscreen)"
                className="ba-lightbox__img"
                onClick={(e) => e.stopPropagation()}
              />
              {running && zoomSrc === screenshot && <span className="ba-lightbox__live">● live</span>}
            </div>
          )}

          {screenshot && !running && !activeTask && steps.length === 0 && (
            <div className="ba-readyhint">
              {pageTitle
                ? <>Loaded <strong>{pageTitle}</strong> ({labelFor(currentUrl || startUrl)}) — type a task to begin.</>
                : <>Browser is open on {labelFor(startUrl)} — type a task to begin.</>}
            </div>
          )}

          <div className="ba-panel__steps">
            {steps.map((s) => (
              <motion.div
                key={s.step_num}
                className={`ba-step ${s.ok ? '' : 'ba-step--err'}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <span className="ba-step__icon">{ACTION_ICON[s.action] ?? '•'}</span>
                <div className="ba-step__body">
                  <div className="ba-step__head">
                    <span className="ba-step__num">#{s.step_num}</span>
                    <span className="ba-step__action">{s.action}</span>
                    {s.target && <span className="ba-step__target">{s.target}</span>}
                  </div>
                  {s.reasoning && <div className="ba-step__reason">{s.reasoning}</div>}
                  {s.result_text && <div className="ba-step__result">{s.result_text}</div>}
                </div>
              </motion.div>
            ))}

            {running && !inputReq && extractProgress && (
              <div className="ba-extract-progress">
                ⊞ Extracted {extractProgress.count} record{extractProgress.count === 1 ? '' : 's'} across {extractProgress.page} page{extractProgress.page === 1 ? '' : 's'}…
              </div>
            )}
            {running && !inputReq && <div className="ba-panel__thinking"><span /><span /><span /></div>}

            {result && (
              <motion.div
                className={`ba-result ba-result--${result.status}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="ba-result__head">{result.status === 'success' ? '✅ Done' : '⛔ Could not finish'}</div>
                <div className="ba-result__body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.output || ''}</ReactMarkdown>
                </div>
                <ExtractResult data={result.data} schema={activeSchema} output={activeOutput} errors={schemaErrors} />
              </motion.div>
            )}

            {error && (
              <div className="ba-result ba-result--failed">
                <div className="ba-result__head">⛔ Error</div>
                <div className="ba-result__body">{error}</div>
                {canResume && !running && (
                  <button className="ba-resume-btn" onClick={() => resumeRef.current?.()}>
                    ↻ Resume from where it left off
                  </button>
                )}
              </div>
            )}
            <div ref={endRef} />
          </div>
        </div>

        {inputReq && (
          <InputRequestCard req={inputReq} onSubmit={submitInput} onCancel={() => setInputReq(null)} />
        )}

        <div className="ba-panel__compose ba-center__compose">
          <div className="ba-startbar">
            <span className="ba-startbar__site" title={startUrl}>▶ Start: {labelFor(startUrl)}</span>
            <button
              className="ba-startbar__change"
              onClick={changeSite}
              disabled={running}
            >
              Change site
            </button>
          </div>
          <div className="ba-panel__taskrow">
            <textarea
              className="ba-panel__task"
              placeholder="e.g. Open the Pricing page and list the plan names  (Enter to send, Shift+Enter for newline)"
              value={task}
              onChange={(e) => setTask(e.target.value)}
              onKeyDown={onTaskKeyDown}
              disabled={running}
              rows={2}
              autoFocus
            />
            {running ? (
              <button className="ba-panel__run ba-panel__stop" onClick={stop} title="Stop the run">
                <span className="ba-stop-icon" /> Stop
              </button>
            ) : (
              <button className="ba-panel__run" onClick={() => handleRun()} disabled={!task.trim() || !startUrl}>
                <span className="ba-run-icon">▶</span>
              </button>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

// ── Site picker (shown until a start site is chosen) ───────────────────────────
const SitePicker: React.FC<{
  sites: AgentSite[];
  notice?: string;
  onPick: (site: AgentSite) => void;
  onOpenUrl: (url: string) => void;
}> = ({ sites, notice, onPick, onOpenUrl }) => {
  const [url, setUrl] = useState('');
  const submit = () => { if (url.trim()) onOpenUrl(url); };
  return (
    <div className="ba-picker">
      <div className="ba-picker__title">Where should the agent start?</div>
      <div className="ba-picker__subtitle">
        Pick a saved site (it logs in with the default login), or open any URL directly — no login.
        Manage sites &amp; logins in the right panel.
      </div>
      {notice && <div className="ba-needlogin">{notice}</div>}
      <div className="ba-picker__grid">
        {sites.map((s) => {
          const def = getDefaultLogin(s);
          return (
            <button key={s.id} className="ba-picker__tile" onClick={() => onPick(s)} title={s.loginUrl}>
              <span className="ba-picker__tile-label">{s.name}</span>
              <span className="ba-picker__tile-host">{hostOf(s.loginUrl)}</span>
              <span className={`ba-picker__tile-badge ${def ? 'is-ready' : ''}`}>
                {def ? `🔐 ${def.username}` : '+ add login'}
              </span>
            </button>
          );
        })}
      </div>

      <div className="ba-picker__url">
        <span className="ba-picker__url-label">Or open any URL — no login</span>
        <div className="ba-picker__url-row">
          <input
            className="ba-picker__url-input"
            type="text"
            placeholder="example.com  or  https://example.com/page"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
            autoComplete="off"
          />
          <button className="ba-picker__url-btn" onClick={submit} disabled={!url.trim()}>Open</button>
        </div>
      </div>
    </div>
  );
};

function hostOf(url: string): string {
  try { return new URL(url).host; } catch { return url; }
}
function labelFor(url: string): string {
  const s = listSites().find((x) => x.loginUrl === url);
  return s ? s.name : hostOf(url);
}

// ── Extracted-data JSON block (copy / download) ────────────────────────────────
const JsonResult: React.FC<{ data: any }> = ({ data }) => {
  const [copied, setCopied] = useState(false);
  const text = (() => { try { return JSON.stringify(data, null, 2); } catch { return String(data); } })();
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* */ }
  };
  const download = () => {
    try {
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'extracted-data.json'; a.click();
      URL.revokeObjectURL(url);
    } catch { /* */ }
  };
  return (
    <div className="ba-json">
      <div className="ba-json__bar">
        <span className="ba-json__label">Extracted data (JSON)</span>
        <div className="ba-json__actions">
          <button onClick={copy}>{copied ? '✓ Copied' : 'Copy'}</button>
          <button onClick={download}>Download</button>
        </div>
      </div>
      <pre className="ba-json__pre">{text}</pre>
    </div>
  );
};

// ── Extracted records: table (with CSV/JSON export) or JSON fallback ───────────
function cellText(v: any): string {
  if (v === null || v === undefined) return '';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}
function toCsv(rows: any[], columns: string[]): string {
  const esc = (v: any) => {
    const s = cellText(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = columns.map(esc).join(',');
  const body = rows.map((r) => columns.map((c) => esc(r?.[c])).join(',')).join('\n');
  return `${head}\n${body}`;
}
function downloadText(content: string, type: string, filename: string): void {
  try {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch { /* */ }
}

const RecordsTable: React.FC<{ rows: any[]; schema?: FieldDef[] | null; errors?: any[] }> = ({ rows, schema, errors }) => {
  const [copied, setCopied] = useState(false);
  const columns = (schema && schema.length)
    ? schema.map((f) => f.name)
    : Array.from(rows.reduce((set: Set<string>, r) => { Object.keys(r || {}).forEach((k) => set.add(k)); return set; }, new Set<string>()));
  const badRows = new Set<number>((errors || []).map((e) => e.index));
  const json = (() => { try { return JSON.stringify(rows, null, 2); } catch { return '[]'; } })();
  const copyJson = async () => {
    try { await navigator.clipboard.writeText(json); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* */ }
  };
  return (
    <div className="ba-rt">
      <div className="ba-rt__bar">
        <span className="ba-rt__label">{rows.length} record{rows.length === 1 ? '' : 's'}{badRows.size ? ` · ${badRows.size} with issues` : ''}</span>
        <div className="ba-rt__actions">
          <button onClick={() => downloadText(toCsv(rows, columns), 'text/csv', 'extracted-data.csv')}>Download CSV</button>
          <button onClick={() => downloadText(json, 'application/json', 'extracted-data.json')}>Download JSON</button>
          <button onClick={copyJson}>{copied ? '✓ Copied' : 'Copy JSON'}</button>
        </div>
      </div>
      <div className="ba-rt__scroll">
        <table className="ba-rt__table">
          <thead>
            <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={badRows.has(i) ? 'ba-rt__row--bad' : ''} title={badRows.has(i) ? 'This row had validation issues' : undefined}>
                {columns.map((c) => <td key={c}>{cellText(r?.[c])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ExtractResult: React.FC<{ data: any; schema?: FieldDef[] | null; output?: 'table' | 'json'; errors?: any[] }> = ({ data, schema, output, errors }) => {
  if (data == null) return null;
  const isRecordArray = Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null;
  if (isRecordArray && output !== 'json') {
    return <RecordsTable rows={data} schema={schema} errors={errors} />;
  }
  return <JsonResult data={data} />;
};

// ── Skill page (center: view / edit / run a saved skill) ───────────────────────
const SkillPage: React.FC<{
  initial: { id?: string; name: string; prompt: string };
  onRun: (prompt: string) => void;
  onClose: () => void;
}> = ({ initial, onRun, onClose }) => {
  const [name, setName] = useState(initial.name);
  const [prompt, setPrompt] = useState(initial.prompt);
  const isNew = !initial.id;
  const canSave = name.trim() && prompt.trim();
  const save = () => { if (canSave) saveSkill({ id: initial.id, name: name.trim(), prompt: prompt.trim() }); };

  return (
    <div className="ba-center__col">
      <div className="ba-runview__bar">
        <span className="ba-runview__title">⚡ {isNew ? 'New skill' : 'Skill'}</span>
        <button className="ba-runview__back" onClick={onClose}>← Back</button>
      </div>
      <div className="ba-center__scroll">
        <div className="ba-skillpage">
          <label className="ba-skillpage__label">Name</label>
          <input className="ba-skillpage__field" placeholder="Skill name" value={name} onChange={(e) => setName(e.target.value)} />
          <label className="ba-skillpage__label">Task prompt</label>
          <textarea className="ba-skillpage__field ba-skillpage__prompt" rows={6} placeholder="What should the agent do? (it runs in your current session)" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
          <div className="ba-skillpage__actions">
            {!isNew && (
              <button className="ba-skillpage__btn ba-skillpage__btn--danger" onClick={() => { deleteSkill(initial.id!); onClose(); }}>Delete</button>
            )}
            <span style={{ flex: 1 }} />
            <button className="ba-skillpage__btn ba-skillpage__btn--ghost" onClick={save} disabled={!canSave}>Save</button>
            <button className="ba-skillpage__btn" onClick={() => { save(); onRun(prompt.trim()); }} disabled={!prompt.trim()}>▶ Run skill</button>
          </div>
          <div className="ba-skillpage__hint">Saved skills appear under Browser Agent in the left panel. Run executes the prompt in your current session.</div>
        </div>
      </div>
    </div>
  );
};

// ── Past-run view (center, read-only) ──────────────────────────────────────────
const RunView: React.FC<{ run: RunDetail; onBack: () => void }> = ({ run, onBack }) => {
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!zoomSrc) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoomSrc(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomSrc]);
  const shotSteps = run.steps.filter((s) => s.has_screenshot);
  return (
  <div className="ba-center__col">
    <div className="ba-runview__bar">
      <span className="ba-runview__title">📁 Past run · {run.status}</span>
      <button className="ba-runview__back" onClick={onBack}>← Back to session</button>
    </div>
    <div className="ba-center__scroll">
      <div className="ba-activetask"><span className="ba-activetask__icon">▶</span>{run.task}</div>
      <img
        className="ba-panel__shot ba-center__shot ba-shot__img--zoomable"
        src={runScreenshotUrl(run.run_id)}
        alt="Final screenshot"
        title="Click to view fullscreen"
        onClick={() => setZoomSrc(runScreenshotUrl(run.run_id))}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
      {shotSteps.length > 0 && (
        <div className="ba-filmstrip" aria-label="Screenshot history">
          {shotSteps.map((s) => {
            const url = stepScreenshotUrl(run.run_id, s.step_num);
            return (
              <button
                key={s.step_num}
                type="button"
                className="ba-filmstrip__item"
                title={`Step ${s.step_num} · ${s.action} — click to enlarge`}
                onClick={() => setZoomSrc(url)}
              >
                <img src={url} alt={`Step ${s.step_num}`} loading="lazy" />
                <span className="ba-filmstrip__num">{s.step_num}</span>
              </button>
            );
          })}
        </div>
      )}
      {run.result?.output && (
        <div className={`ba-result ba-result--${run.status === 'success' ? 'success' : 'failed'}`}>
          <div className="ba-result__head">{run.status === 'success' ? '✅ Result' : '⛔ Result'}</div>
          <div className="ba-result__body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{run.result.output}</ReactMarkdown></div>
        </div>
      )}
      {run.result?.data != null && (
        <ExtractResult
          data={run.result.data}
          schema={run.result.schema}
          output={Array.isArray(run.result.schema) && run.result.schema.length ? 'table' : 'json'}
          errors={run.result.schema_errors}
        />
      )}
      {run.error_message && <div className="ba-result ba-result--failed"><div className="ba-result__body">{run.error_message}</div></div>}
      <div className="ba-panel__steps">
        {run.steps.map((s) => (
          <div key={s.step_num} className={`ba-step ${s.ok ? '' : 'ba-step--err'}`}>
            <span className="ba-step__icon">{ACTION_ICON[s.action] ?? '•'}</span>
            <div className="ba-step__body">
              <div className="ba-step__head">
                <span className="ba-step__num">#{s.step_num}</span>
                <span className="ba-step__action">{s.action}</span>
                {s.target && <span className="ba-step__target">{s.target}</span>}
              </div>
              {s.result_text && <div className="ba-step__result">{s.result_text}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
    {zoomSrc && (
      <div className="ba-lightbox" onClick={() => setZoomSrc(null)}>
        <button type="button" className="ba-lightbox__close" title="Close (Esc)" onClick={() => setZoomSrc(null)}>✕</button>
        <img src={zoomSrc} alt="Screenshot (fullscreen)" className="ba-lightbox__img" onClick={(e) => e.stopPropagation()} />
      </div>
    )}
  </div>
  );
};

// ── Copyable current-URL bar ───────────────────────────────────────────────────
const CopyableUrl: React.FC<{ url: string }> = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div className="ba-urlbar">
      <span className="ba-urlbar__icon">🔗</span>
      <span className="ba-urlbar__text" title={url}>{url}</span>
      <button className="ba-urlbar__copy" onClick={copy} title="Copy URL">{copied ? '✓' : '⧉'}</button>
    </div>
  );
};

// ── Human-in-the-loop input card (text question or credential picker) ──────────
const InputRequestCard: React.FC<{
  req: InputRequest;
  onSubmit: (p: InputPayload) => void;
  onCancel: () => void;
}> = ({ req, onSubmit, onCancel }) => {
  const [text, setText] = useState('');
  const site = findSiteByDomain(req.domain);
  const [logins, setLogins] = useState<SiteLogin[]>(() => (site ? site.logins : []));
  const [adding, setAdding] = useState(() => !site || site.logins.length === 0);
  const [label, setLabel] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  if (req.inputKind === 'credentials') {
    const chooseLogin = (l: SiteLogin) =>
      onSubmit({ kind: 'credentials', username: l.username, password: l.password });
    const addAndUse = () => {
      if (!username || !password) return;
      if (site) {
        addLogin(site.id, { label, username, password });
        setLogins(findSiteByDomain(req.domain)?.logins ?? []);
      }
      onSubmit({ kind: 'credentials', username, password });
    };

    return (
      <div className="ba-input">
        <div className="ba-input__head">🔐 {req.prompt}</div>
        <div className="ba-input__domain">for {site ? site.name : (req.domain || 'this site')}</div>

        {logins.length > 0 && !adding && (
          <div className="ba-input__creds">
            {logins.map((l) => (
              <button key={l.id} className="ba-cred__use" onClick={() => chooseLogin(l)}>
                <span className="ba-cred__label">{l.label}</span>
                <span className="ba-cred__user">{l.username}</span>
              </button>
            ))}
            <button className="ba-input__addlink" onClick={() => setAdding(true)}>+ Use a different login</button>
          </div>
        )}

        {adding && (
          <div className="ba-input__form">
            <input className="ba-input__field" placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
            <input className="ba-input__field" placeholder="Username / email" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
            <input className="ba-input__field" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            <div className="ba-input__actions">
              {logins.length > 0 && (
                <button className="ba-input__btn ba-input__btn--ghost" onClick={() => setAdding(false)}>Back</button>
              )}
              <button className="ba-input__btn" onClick={addAndUse} disabled={!username || !password}>
                {site ? 'Save & use' : 'Use'}
              </button>
            </div>
            {site && <div className="ba-input__note">Saved to {site.name} in this browser.</div>}
          </div>
        )}

        <button className="ba-input__cancel" onClick={onCancel}>Dismiss</button>
      </div>
    );
  }

  return (
    <div className="ba-input">
      <div className="ba-input__head">💬 {req.prompt}</div>
      <div className="ba-input__form">
        <textarea
          className="ba-input__field"
          rows={2}
          placeholder="Type your answer… (Enter to send)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (text.trim()) onSubmit({ kind: 'text', text }); }
          }}
        />
        <div className="ba-input__actions">
          <button className="ba-input__btn ba-input__btn--ghost" onClick={onCancel}>Dismiss</button>
          <button className="ba-input__btn" onClick={() => onSubmit({ kind: 'text', text })} disabled={!text.trim()}>Send</button>
        </div>
      </div>
    </div>
  );
};

export default BrowserAgentPage;
